import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Returns a translator scoped to the 'errors' namespace.
 * Falls back to UNKNOWN for any unmapped code, including future backend codes
 * not yet added to errors.json.
 *
 * Usage:
 *   const tError = useErrorTranslation();
 *   tError(extractGqlErrorCode(error))          // → user-facing string
 *   tError('title')                             // → "Error"
 *   tError('PAYMENT_RECEIPT_FAILED', { phone })  // → interpolated message
 */
export function useErrorTranslation(): (code: string, options?: Record<string, unknown>) => string {
  const { t } = useTranslation('errors');
  // Memoized so callers can safely use tError as a useEffect/useCallback dependency
  // without it forcing a re-run on every render.
  return useCallback(
    (code: string, options?: Record<string, unknown>): string => t(code, { defaultValue: t('UNKNOWN'), ...options }),
    [t],
  );
}
